import './securityParams.scss'

import { Box, Button, Divider, MenuItem, Modal, Paper, Select, Switch, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import EditIcon from '@mui/icons-material/Edit'
import { useDispatch, useSelector } from 'react-redux'
import { performSetSecurityParameters } from '../../../features/auth/loginSlice'
import AlertFetchSpinner from '../../../components/common/alertFetchSpinner/alertFetchSpinner'
import Spinner from '../../../components/common/spinner/spinner'
import { useNavigate } from 'react-router-dom'
import DataExportModal from '../../../components/common/dataExport/DataExportModal'
import {
  securityParameterReportDataMapper,
  securityParameterReportDataMapperForPDF,
} from '../../../components/common/dataExport/dataMappers/securityParametersReportDataMapper'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import { useTranslation } from 'react-i18next'
import { getClientKeyWithAccessToken } from '../../../lib/security'

const SecurityParams = () => {
  const { accessToken, emailClient, securityParameters, activeServices } = useSelector((state) => state.login)
  const [fetchError, setFetchError] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [fetchMessage, setFetchMessage] = useState('')
  const [formData, setFormData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'messages'])
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  if (!activeServices || !activeServices.includes('usermaster:emailClient')) navigate('/', { replace: true })

  const handleFetch = (error, message) => {
    setIsFetching(true)
    setFetchError(error)
    setFetchMessage(message)
    setTimeout(() => {
      setIsFetching(false)
    }, 3000)
  }

  const handleSwitch = (type) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [type]: {
        ...prevFormData[type],
        value: !prevFormData[type].value,
      },
    }))
  }

  const handleChange = (value, type) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [type]: {
        ...prevFormData[type],
        value,
      },
    }))
  }

  useEffect(() => {
    if (!securityParameters || !Object.keys(securityParameters).length) {
      getParameters()
    }
  }, [])

  useEffect(() => {
    if (securityParameters && Object.keys(securityParameters).length) {
      const additionalInfo = {
        checkPasswordHistoryLength: {
          value: securityParameters.checkPasswordHistoryLength || false,
          title: t('common:checkPasswordHistoryLengthTitle'),
          description: t('common:checkPasswordHistoryLengthDescription'),
        },
        passwordHistoryLength: {
          value: securityParameters.passwordHistoryLength,
          title: t('common:passwordHistoryLengthTitle'),
          description: t('common:passwordHistoryLengthDescription'),
        },
        checkPasswordExpirationDays: {
          value: securityParameters.checkPasswordExpirationDays || false,
          title: t('common:checkPasswordExpirationDaysTitle'),
          description: t('common:checkPasswordExpirationDaysDescription'),
        },
        passwordExpirationDays: {
          value: securityParameters.passwordExpirationDays,
          title: t('common:passwordExpirationDaysTitle'),
          description: t('common:passwordExpirationDaysDescription'),
        },
        requireInactiveDuration: {
          value: securityParameters.requireInactiveDuration || false,
          title: t('common:requireInactiveDurationTitle'),
          description: t('common:requireInactiveDurationDescription'),
        },
        maxDaysToInactive: {
          value: securityParameters.maxDaysToInactive,
          title: t('common:maxDaysToInactiveTitle'),
          description: t('common:maxDaysToInactiveDescription'),
        },
        maxFailedAttemptsRequired: {
          value: securityParameters.maxFailedAttemptsRequired || false,
          title: t('common:maxFailedAttemptsRequiredTitle'),
          description: t('common:maxFailedAttemptsRequiredDescription'),
        },
        maxFailedAttempts: {
          value: securityParameters.maxFailedAttempts,
          title: t('common:maxFailedAttemptsTitle'),
          description: t('common:maxFailedAttemptsDescription'),
        },
        requirePasswordChangeOnFirstLogin: {
          value: securityParameters.requirePasswordChangeOnFirstLogin || false,
          title: t('common:requirePasswordChangeOnFirstLoginTitle'),
          description: t('common:requirePasswordChangeOnFirstLoginDescription'),
        },
        dashboardActivity: {
          value: securityParameters.dashboardActivity || false,
          title: t('common:dashboardActivityTitle'),
          description: t('common:dashboardActivityDescription'),
        },
        activityTime: {
          value: securityParameters.activityTime,
          title: t('common:activityTimeTitle'),
          description: t('common:activityTimeDescription'),
        },
        requireUserEmailNotification: {
          value: securityParameters.requireUserEmailNotification,
          title: t('common:requireUserEmailNotificationTitle'),
          description: t('common:requireUserEmailNotificationDescription'),
        },
        enableTransactionStates: {
          value: securityParameters.enableTransactionStates,
          title: t('common:enableTransactionStatesTitle'),
          description: t('common:enableTransactionStatesDescription'),
        },
        hideNewPassword: {
          value: securityParameters.hideNewPassword,
          title: t('common:hideNewPasswordTitle'),
          description: t('common:hideNewPasswordDescription'),
        },
      }
      setErrorMessage('')
      setFormData(additionalInfo)
    }
  }, [securityParameters, t])

  const getParameters = async () => {
    setIsLoading(true)
    const urlUserMaster = `${process.env.REACT_APP_BASEURL}/usermaster/${emailClient}`
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'client-key': await getClientKeyWithAccessToken(accessToken),
      },
    }

    fetch(process.env.REACT_APP_IS_DEMO ? '../data/usermaster.json' : urlUserMaster, options)
      .then((res) => res.json())
      .then((res) => {
        if (res.data === 'Forbidden resource') {
          setErrorMessage(t('messages:dontHavePermissions'))
          setIsLoading(false)
        }
        if (!res.success && res.message && res.message.length !== 0) {
          setIsLoading(false)
          handleFetch(true, res.message ? res.message : t('messages:updateSecurityError'))
          throw Error(res.message.toString())
        } else {
          setErrorMessage('')
          dispatch(performSetSecurityParameters(res))
          setIsLoading(false)
          handleFetch(false, t('messages:updateSecuritySuccess'))
        }
      })
      .catch((error) => {
        console.error('[GET SECURITY PARAMETERS ERROR] --> ', error)
        handleFetch(true, error.message ? error.message : t('messages:updateSecurityError'))
      })
  }

  const updateParameters = async (data) => {
    setIsLoading(true)
    if (!process.env.REACT_APP_IS_DEMO) {
      const urlUserMaster = `${process.env.REACT_APP_BASEURL}/usermaster/${emailClient}`
      const options = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'client-key': await getClientKeyWithAccessToken(accessToken),
        },
        body: JSON.stringify(data),
      }

      fetch(urlUserMaster, options)
        .then((res) => res.json())
        .then((res) => {
          if (!res.success && res.message && res.message.length !== 0) {
            setIsLoading(false)
            handleFetch(true, res.message ? res.message : t('messages:updateSecurityError'))
            throw Error(res.message.toString())
          }
          dispatch(performSetSecurityParameters(res.data))
          setIsLoading(false)
          handleFetch(false, t('messages:updateSecuritySuccess'))
        })
        .catch((error) => {
          console.error('[UPDATE SECURITY PARAMETERS ERROR] --> ', error)
          handleFetch(true, error.message ? error.message : t('messages:updateSecurityError'))
          setIsLoading(false)
        })
    } else {
      dispatch(performSetSecurityParameters(data))
      setIsLoading(false)
      handleFetch(false, t('messages:updateSecuritySuccess'))
    }
    getParameters()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const data = {
      checkPasswordHistoryLength: formData.checkPasswordHistoryLength.value,
      passwordHistoryLength: formData.passwordHistoryLength.value,
      checkPasswordExpirationDays: formData.checkPasswordExpirationDays.value,
      passwordExpirationDays: formData.passwordExpirationDays.value,
      requireInactiveDuration: formData.requireInactiveDuration.value,
      maxDaysToInactive: formData.maxDaysToInactive.value,
      maxFailedAttemptsRequired: formData.maxFailedAttemptsRequired.value,
      maxFailedAttempts: formData.maxFailedAttempts.value,
      requirePasswordChangeOnFirstLogin: formData.requirePasswordChangeOnFirstLogin.value,
      dashboardActivity: formData.dashboardActivity.value,
      activityTime: formData.activityTime.value,
      requireUserEmailNotification: formData.requireUserEmailNotification.value,
      enableTransactionStates: formData.enableTransactionStates.value,
      hideNewPassword: formData.hideNewPassword.value,
    }

    setEdit(false)
    updateParameters(data)
    getParameters()
  }

  const renderTooltip = (title) => (
    <Tooltip title={title}>
      <Button>
        <InfoIcon className="security-info-icon" />
      </Button>
    </Tooltip>
  )

  const NumberSelect = ({ min, max, defaultValue, type, disabled }) => {
    const numbers = Array.from({ length: max - min + 1 }, (_, index) => index + 1)

    return (
      <Select
        disabled={!disabled || !edit}
        id={type}
        key={type}
        size="small"
        style={{ minWidth: '4rem' }}
        defaultValue={defaultValue ?? ''}
        onChange={(e) => handleChange(e.target.value, type)}
      >
        {numbers.map((number) => (
          <MenuItem key={number} value={number}>
            {number}
          </MenuItem>
        ))}
      </Select>
    )
  }

  return (
    <Box className={`${isLoading && 'spinner-transition'} filter`}>
      {isLoading && <Spinner noTransform />}

      {isFetching && <AlertFetchSpinner message={fetchMessage} error={fetchError} />}

      <Box sx={{ marginBottom: '0rem!important' }}>
        <Box className="component-title">
          <Typography variant="h2">{t('common:securityParameters')}</Typography>
        </Box>
        <Typography variant="subtitle3">
          {/* Loren ipsum dolor sit amet Loren ipsum dolor sit amet Loren  */}
        </Typography>
      </Box>

      {errorMessage ? (
        <Box className="security-error">
          <Button size="small" variant="outlined" color="error">
            {errorMessage}
          </Button>
        </Box>
      ) : (
        <Box className="alert-settings-overflow">
          <Box className="security-bar">
            <Box>
              <Button
                variant="outlined"
                disabled={!activeServices.includes('reportsitem') || !formData}
                onClick={() => setShowDownloadModal(true)}
                endIcon={<IosShareOutlinedIcon sx={{ fill: 'var(--lime-04)' }} />}
              >
                {t('common:exportList')}
              </Button>
            </Box>
            <Button
              variant="outlinedButtonSuccess"
              onClick={() => {
                setEdit(true)
              }}
            >
              <EditIcon />
            </Button>
          </Box>

          <Paper elevation={0} className="security-buttons">
            <Box className="security-grid">
              <Box>
                <Box className="security-flex">
                  <p>{formData.checkPasswordHistoryLength?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.checkPasswordHistoryLength?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      disabled={!edit}
                      color="secondary"
                      name="checkPasswordHistoryLength"
                      id="checkPasswordHistoryLength"
                      onChange={() => handleSwitch('checkPasswordHistoryLength')}
                      checked={formData.checkPasswordHistoryLength?.value || false}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.passwordHistoryLength?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.passwordHistoryLength?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <NumberSelect
                      min={1}
                      max={12}
                      defaultValue={formData.passwordHistoryLength?.value || 1}
                      type="passwordHistoryLength"
                      disabled={formData.checkPasswordHistoryLength?.value}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.checkPasswordExpirationDays?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.checkPasswordExpirationDays?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      color="secondary"
                      disabled={!edit}
                      name="checkPasswordExpirationDays"
                      onChange={() => handleSwitch('checkPasswordExpirationDays')}
                      checked={formData.checkPasswordExpirationDays?.value || false}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.passwordExpirationDays?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.passwordExpirationDays?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <NumberSelect
                      min={1}
                      max={180}
                      defaultValue={formData.passwordExpirationDays?.value || 1}
                      type="passwordExpirationDays"
                      disabled={formData.checkPasswordExpirationDays?.value}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.requireInactiveDuration?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.requireInactiveDuration?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      color="secondary"
                      disabled={!edit}
                      name="requireInactiveDuration"
                      onChange={() => handleSwitch('requireInactiveDuration')}
                      checked={formData.requireInactiveDuration?.value || false}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.maxDaysToInactive?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.maxDaysToInactive?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <NumberSelect
                      min={1}
                      max={180}
                      defaultValue={formData.maxDaysToInactive?.value || 1}
                      type="maxDaysToInactive"
                      disabled={formData.requireInactiveDuration?.value}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.maxFailedAttemptsRequired?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.maxFailedAttemptsRequired?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      color="secondary"
                      disabled={!edit}
                      name="maxFailedAttemptsRequired"
                      onChange={() => handleSwitch('maxFailedAttemptsRequired')}
                      checked={formData.maxFailedAttemptsRequired?.value || false}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.maxFailedAttempts?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.maxFailedAttempts?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <NumberSelect
                      min={1}
                      max={30}
                      defaultValue={formData.maxFailedAttempts?.value || 1}
                      type="maxFailedAttempts"
                      disabled={formData.maxFailedAttemptsRequired?.value}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p> {formData.dashboardActivity?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.dashboardActivity?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      color="secondary"
                      disabled={!edit}
                      name="dashboardActivity"
                      onChange={() => handleSwitch('dashboardActivity')}
                      checked={formData.dashboardActivity?.value || false}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.activityTime?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.activityTime?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <NumberSelect
                      min={1}
                      max={30}
                      defaultValue={formData.activityTime?.value || 1}
                      type="activityTime"
                      disabled={formData.dashboardActivity?.value}
                    />
                  </Box>
                </Box>

                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.requirePasswordChangeOnFirstLogin?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.requirePasswordChangeOnFirstLogin?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      disabled={!edit}
                      color="secondary"
                      name="requirePasswordChangeOnFirstLogin"
                      onChange={() => handleSwitch('requirePasswordChangeOnFirstLogin')}
                      checked={formData.requirePasswordChangeOnFirstLogin?.value || false}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.requireUserEmailNotification?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.requireUserEmailNotification?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      disabled={!edit}
                      color="secondary"
                      name="requireUserEmailNotification"
                      onChange={() => handleSwitch('requireUserEmailNotification')}
                      checked={formData.requireUserEmailNotification?.value || false}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>

              <Box className="security-flex">
                <p>{formData.enableTransactionStates?.title || ''}</p>
                <Box className="security-flex-label">
                  {renderTooltip(
                    <Box className="back-tip">
                      <label className="security-items-info" color="inherit">
                        {formData.enableTransactionStates?.description || ''}
                      </label>
                    </Box>,
                  )}
                  <Switch
                    disabled={!edit}
                    color="secondary"
                    name="enableTransactionStates"
                    onChange={() => handleSwitch('enableTransactionStates')}
                    checked={formData.enableTransactionStates?.value || false}
                  />
                </Box>
              </Box>

              <Box>
                <Box className="security-flex">
                  <p>{formData.hideNewPassword?.title || ''}</p>
                  <Box className="security-flex-label">
                    {renderTooltip(
                      <Box className="back-tip">
                        <label className="security-items-info" color="inherit">
                          {formData.hideNewPassword?.description || ''}
                        </label>
                      </Box>,
                    )}
                    <Switch
                      disabled={!edit}
                      color="secondary"
                      name="hideNewPassword"
                      onChange={() => handleSwitch('hideNewPassword')}
                      checked={formData.hideNewPassword?.value || false}
                    />
                  </Box>
                </Box>
                <Divider style={{ margin: '.6rem 0rem' }} />
              </Box>
            </Box>

            {edit ? (
              <Box className="modal-buttons" sx={{ marginTop: '1rem' }}>
                <Button
                  onClick={() => {
                    setEdit(false)
                    return getParameters()
                  }}
                  size="small"
                  variant="outlinedGrey"
                >
                  {t('common:cancel')}
                </Button>
                <Button size="small" variant="contained" type="submit" onClick={(e) => handleSubmit(e)}>
                  {t('common:save')}
                </Button>
              </Box>
            ) : (
              ''
            )}
          </Paper>
        </Box>
      )}
      <Modal open={showDownloadModal} onClose={() => setShowDownloadModal(false)}>
        <div>
          <DataExportModal
            show={showDownloadModal}
            onClose={() => setShowDownloadModal(false)}
            data={formData}
            dataMapper={securityParameterReportDataMapper}
            dataMapperForPDF={securityParameterReportDataMapperForPDF}
            fileName={'security_parameters_report'}
          />
        </div>
      </Modal>
    </Box>
  )
}

export default SecurityParams
